import React, { useState, useMemo } from 'react';
import { X, ArrowLeftRight } from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';

interface Props {
  dark?: boolean;
  onClose: () => void;
}

const fmtSym = (s: string) =>
  s.replace('PERP_', '').replace('_USDC', '').replace('-PERP', '');

const fmtPrice = (v: number) =>
  v === 0 ? '—' : v < 1 ? v.toFixed(5) : v.toFixed(4);

export default function PairTradeModal({ dark = true, onClose }: Props) {
  const { tickers: rawTickers, sendWs } = useWS();
  const t = getTheme(dark);

  const tickers = useMemo(() => {
    const m: Record<string, any> = {};
    (rawTickers as any[]).forEach((tk: any) => { m[tk.symbol] = tk; });
    return m;
  }, [rawTickers]);

  const symbols = useMemo(() =>
    rawTickers.map(tk => tk.symbol).sort(), [rawTickers]
  );

  const [name,      setName]      = useState('');
  const [symA,      setSymA]      = useState(symbols[0] || '');
  const [dirA,      setDirA]      = useState<'long' | 'short'>('long');
  const [symB,      setSymB]      = useState(symbols[1] || '');
  const [dirB,      setDirB]      = useState<'long' | 'short'>('short');
  const [notional,  setNotional]  = useState('100');
  const [leverage,  setLeverage]  = useState('1');
  const [epA,       setEpA]       = useState('');
  const [epB,       setEpB]       = useState('');

  const tkA = tickers[symA];
  const tkB = tickers[symB];

  const markA = tkA?.mark_price || 0;
  const markB = tkB?.mark_price || 0;

  const entryA = parseFloat(epA) || (dirA === 'long' ? tkA?.best_bid : tkA?.best_ask) || markA;
  const entryB = parseFloat(epB) || (dirB === 'long' ? tkB?.best_bid : tkB?.best_ask) || markB;

  const ntl  = parseFloat(notional) || 0;
  const qtyA = entryA > 0 ? (ntl / entryA) : 0;
  const qtyB = entryB > 0 ? (ntl / entryB) : 0;
  const ratio = entryA > 0 && entryB > 0 ? entryA / entryB : 0;

  const canSubmit = symA && symB && symA !== symB && ntl > 0 && entryA > 0 && entryB > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    sendWs({
      type: 'create_pair_trade',
      payload: {
        name:         name || `${fmtSym(symA)}/${fmtSym(symB)}`,
        notional_usd: ntl,
        leverage:     parseInt(leverage) || 1,
        leg_a: { symbol: symA, direction: dirA, entry_price: entryA },
        leg_b: { symbol: symB, direction: dirB, entry_price: entryB },
      },
    });
    onClose();
  };

  const inp   = `border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${t.inp}`;
  const card  = `${t.card} border ${t.cardBorder} rounded-xl`;
  const btnPri = 'bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530] font-bold text-xs py-2 rounded-lg';
  const btnSec = `text-xs py-2 rounded-lg ${t.btnSecondary}`;

  const DirToggle = ({
    value, onChange,
  }: { value: 'long' | 'short'; onChange: (v: 'long' | 'short') => void }) => (
    <div className="flex gap-1">
      {(['long', 'short'] as const).map(d => (
        <button key={d} onClick={() => onChange(d)}
          className={`px-3 py-1 rounded text-xs font-bold border transition-colors ${
            value === d
              ? d === 'long'
                ? 'bg-emerald-600 border-emerald-500 text-white'
                : 'bg-rose-600 border-rose-500 text-white'
              : t.btnSecondary
          }`}>
          {d === 'long' ? 'LONG' : 'SHORT'}
        </button>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}>
      <div className={`${card} p-5 w-[420px] space-y-4`} onClick={e => e.stopPropagation()}>

        {/* Başlık */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowLeftRight size={16} className="text-[#4A9FE8]" />
            <h3 className={`font-bold text-sm ${t.text}`}>Yeni Pair Trade</h3>
          </div>
          <button onClick={onClose} className={`${t.muted} hover:text-rose-400`}>
            <X size={15} />
          </button>
        </div>

        {/* İsim */}
        <div>
          <label className={`text-xs block mb-1 ${t.muted}`}>Strateji Adı (opsiyonel)</label>
          <input className={`${inp} w-full`} placeholder={`${fmtSym(symA)}/${fmtSym(symB)}`}
            value={name} onChange={e => setName(e.target.value)} />
        </div>

        {/* Leg A */}
        <div className={`border ${t.cardBorder} rounded-lg p-3 space-y-2`}>
          <p className={`text-xs font-semibold ${t.muted}`}>LEG A</p>
          <div className="flex gap-2 items-center">
            <select className={`${inp} flex-1`} value={symA} onChange={e => setSymA(e.target.value)}>
              {symbols.map(s => (
                <option key={s} value={s}>{fmtSym(s)}</option>
              ))}
            </select>
            <DirToggle value={dirA} onChange={setDirA} />
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <label className={`text-xs block mb-1 ${t.muted}`}>Giriş Fiyatı ($)</label>
              <input type="number" step="any" className={`${inp} w-full`}
                placeholder={fmtPrice(dirA === 'long' ? tkA?.best_bid || 0 : tkA?.best_ask || 0)}
                value={epA} onChange={e => setEpA(e.target.value)} />
            </div>
            <div className="text-right mt-4">
              <p className={`text-xs ${t.muted}`}>Adet</p>
              <p className={`text-xs font-mono font-bold ${t.text}`}>
                {qtyA > 0 ? qtyA.toFixed(4) : '—'}
              </p>
            </div>
          </div>
          {tkA && (
            <p className={`text-xs ${t.muted}`}>
              Mark: <span className="font-mono">${fmtPrice(markA)}</span>
              &nbsp;·&nbsp;Bid: <span className="font-mono text-emerald-400">${fmtPrice(tkA.best_bid)}</span>
              &nbsp;·&nbsp;Ask: <span className="font-mono text-rose-400">${fmtPrice(tkA.best_ask)}</span>
            </p>
          )}
        </div>

        {/* Leg B */}
        <div className={`border ${t.cardBorder} rounded-lg p-3 space-y-2`}>
          <p className={`text-xs font-semibold ${t.muted}`}>LEG B</p>
          <div className="flex gap-2 items-center">
            <select className={`${inp} flex-1`} value={symB} onChange={e => setSymB(e.target.value)}>
              {symbols.map(s => (
                <option key={s} value={s}>{fmtSym(s)}</option>
              ))}
            </select>
            <DirToggle value={dirB} onChange={setDirB} />
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <label className={`text-xs block mb-1 ${t.muted}`}>Giriş Fiyatı ($)</label>
              <input type="number" step="any" className={`${inp} w-full`}
                placeholder={fmtPrice(dirB === 'long' ? tkB?.best_bid || 0 : tkB?.best_ask || 0)}
                value={epB} onChange={e => setEpB(e.target.value)} />
            </div>
            <div className="text-right mt-4">
              <p className={`text-xs ${t.muted}`}>Adet</p>
              <p className={`text-xs font-mono font-bold ${t.text}`}>
                {qtyB > 0 ? qtyB.toFixed(4) : '—'}
              </p>
            </div>
          </div>
          {tkB && (
            <p className={`text-xs ${t.muted}`}>
              Mark: <span className="font-mono">${fmtPrice(markB)}</span>
              &nbsp;·&nbsp;Bid: <span className="font-mono text-emerald-400">${fmtPrice(tkB.best_bid)}</span>
              &nbsp;·&nbsp;Ask: <span className="font-mono text-rose-400">${fmtPrice(tkB.best_ask)}</span>
            </p>
          )}
        </div>

        {/* Parametreler */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={`text-xs block mb-1 ${t.muted}`}>Notional (USDC, her leg)</label>
            <input type="number" min="1" step="10" className={`${inp} w-full`}
              value={notional} onChange={e => setNotional(e.target.value)} />
          </div>
          <div>
            <label className={`text-xs block mb-1 ${t.muted}`}>Kaldıraç (x)</label>
            <input type="number" min="1" max="20" step="1" className={`${inp} w-full`}
              value={leverage} onChange={e => setLeverage(e.target.value)} />
          </div>
        </div>

        {/* Ratio özet */}
        {ratio > 0 && (
          <div className={`text-xs ${t.muted} border-t ${t.cardBorder} pt-3`}>
            <span>Giriş Ratio: </span>
            <span className={`font-mono font-bold ${t.text}`}>
              1 {fmtSym(symA)} = {ratio.toFixed(4)} {fmtSym(symB)}
            </span>
            <span className="mx-2">·</span>
            <span>Toplam: </span>
            <span className={`font-mono font-bold ${t.text}`}>${(ntl * 2).toFixed(0)} USDC</span>
          </div>
        )}

        {/* Butonlar */}
        <div className="flex gap-2 pt-1">
          <button onClick={handleSubmit} disabled={!canSubmit}
            className={`flex-1 ${btnPri} disabled:opacity-40`}>
            Emirleri Gönder
          </button>
          <button onClick={onClose} className={`flex-1 ${btnSec}`}>İptal</button>
        </div>
      </div>
    </div>
  );
}
